import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet, Routes } from '@angular/router';
import { Location } from '@angular/common';
import { filter } from 'rxjs';
import { nationalModuleRoutes } from './national-module-view.routing';
import { Breadcrumb } from '../../../shared/components/breadcrumb/breadcrumb';
import { Sidebar, SidebarContext } from '../../../shared/components/sidebar/sidebar';
import { FrameworkPrototypeStore } from '../frameworks/framework-prototype.store';

@Component({
  selector: 'app-national-module-view',
  imports: [Breadcrumb, RouterOutlet, Sidebar],
  templateUrl: './national-module-view.html',
})
export class NationalModuleView implements OnInit {
  breadcrumbs: any = [];
  parentRoute: any;

  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly location = inject(Location);
  private readonly frameworks = inject(FrameworkPrototypeStore);

  private readonly url = signal(this.router.url);

  /** The framework being worked in, if any — the sidebar switches to its processes. */
  private readonly activeFramework = computed(() => {
    const path = this.url().split(/[?#]/)[0];
    const prefix = '/mne/national/frameworks/';
    if (!path.startsWith(prefix)) return null;

    const key = path.slice(prefix.length).split('/')[0];
    // Pages such as the SDG tracker live outside a framework's URL but belong to one
    return (
      this.frameworks.framework(key) ??
      this.frameworks
        .frameworks()
        .find((f) =>
          f.processes.some(
            (p) => p.route && (path === p.route || path.startsWith(`${p.route}/`)),
          ),
        ) ??
      null
    );
  });

  readonly menuItems = computed<Routes>(() => {
    const framework = this.activeFramework();
    if (!framework) return nationalModuleRoutes;
    return framework.processes.map((p) => ({
      path: p.route ?? `frameworks/${framework.key}/${p.key}`,
      data: { name: p.name, icon: p.icon, iconType: 'MAT', section: p.group },
    }));
  });

  readonly sidebarContext = computed<SidebarContext | null>(() => {
    const framework = this.activeFramework();
    if (!framework) return null;
    return {
      title: framework.name,
      subtitle: `Tier ${framework.tier} · ${framework.period}`,
      icon: framework.icon,
      backLink: '/mne/national/frameworks',
      backLabel: 'All frameworks',
    };
  });

  ngOnInit(): void {
    if (this.route.routeConfig) {
      const { children, ...otherRouteAttributes } = this.route.routeConfig;
      this.parentRoute = otherRouteAttributes;
    }
    this.setAndUpdateBreadCrumbs();
  }

  goBack(): void {
    this.location.back();
  }

  async setAndUpdateBreadCrumbs() {
    this.breadcrumbs = await this.getBreadcrumbs(this.route);
    this.router.events.pipe(filter((e) => e instanceof NavigationEnd)).subscribe(async () => {
      this.url.set(this.router.url);
      this.breadcrumbs = await this.getBreadcrumbs(this.route);
    });
  }

  async getBreadcrumbs(route: ActivatedRoute): Promise<any> {
    const breadcrumbs: any[] = [];
    let currentRoute = route.root;
    let url = '';

    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
      const path = currentRoute.snapshot.url.map((s) => s.path).join('/');
      if (path) url += `/${path}`;
      // Snapshot data also carries resolved names (framework and process pages)
      const label = currentRoute.snapshot.data?.['name'] ?? currentRoute.routeConfig?.data?.['name'];

      if (label && label != breadcrumbs[breadcrumbs.length - 1]?.label) {
        breadcrumbs.push({ label, url });
      }
    }
    return breadcrumbs;
  }
}
